/**
 * mc-share.js — one delegated handler for every [data-mc-share] on the page.
 *
 * Behaviour:
 *   touch device with navigator.share  → native OS share sheet
 *   otherwise                          → clipboard copy + inline confirmation
 *   no clipboard API                   → hidden textarea + execCommand fallback
 *
 * The snippet emits this script tag on every render, so a page with several
 * share buttons loads it more than once. The MohairShare guard below makes the
 * repeat executions no-ops — without it the delegated listener would stack and
 * every click would fire twice.
 */
(function () {
  'use strict';

  if (window.MohairShare) return;

  var DONE_MS = 2000;

  /**
   * Share URLs are rebuilt from an allowlist rather than copied off
   * location.href, so a parameter we add later for our own purposes never
   * rides along into a link somebody sends to a friend.
   */
  var SHARE_PARAMS = [
    // kit landing
    'k', 'slug', 'via', 'c', 'cw', 'sz', 'o',
    // colour library + yarn collections
    'yarn', 'family', 'stock', 'q', 'view', 'sort',
    // kit index
    'type', 'craft', 'designer', 'collection', 'yarnbase', 'language'
  ];

  var timers = new WeakMap();
  var liveRegion = null;

  function cleanUrl(raw) {
    var u;
    try {
      u = new URL(raw, window.location.href);
    } catch (e) {
      return raw;
    }
    var src = new URLSearchParams(u.search);
    var out = new URLSearchParams();
    // Iterate the allowlist, not the source, so shared links also come out in
    // a stable parameter order.
    SHARE_PARAMS.forEach(function (key) {
      var val = src.get(key);
      if (val !== null && val !== '') out.set(key, val);
    });
    var qs = out.toString();
    return u.origin + u.pathname + (qs ? '?' + qs : '');
  }

  function coarsePointer() {
    try {
      return window.matchMedia('(pointer: coarse)').matches;
    } catch (e) {
      return false;
    }
  }

  function legacyCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }

  function copy(text, done) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        function () { done(true); },
        function () { done(legacyCopy(text)); }
      );
      return;
    }
    done(legacyCopy(text));
  }

  // One page-level live region, created at boot so screen readers have it
  // registered before the first update. Announcing from here rather than from
  // the button keeps the button's accessible name stable ("Share" stays
  // "Share") instead of re-reading the whole control on every copy.
  function announce(message) {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    // Same string twice in a row is otherwise swallowed by some screen readers.
    window.setTimeout(function () { liveRegion.textContent = message; }, 30);
  }

  function confirmCopy(btn) {
    var done = btn.getAttribute('data-mc-share-done') ||
      ((window.MC_I18N && window.MC_I18N.share && window.MC_I18N.share.done) || '');
    btn.classList.add('is-done');
    announce(done);
    window.clearTimeout(timers.get(btn));
    timers.set(btn, window.setTimeout(function () {
      btn.classList.remove('is-done');
    }, DONE_MS));
  }

  function track(btn, method) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'mc_share',
        entity: btn.getAttribute('data-mc-share-entity') || '',
        surface: btn.getAttribute('data-mc-share-surface') || '',
        method: method
      });
    } catch (e) { /* analytics is never worth breaking a share over */ }
  }

  function share(btn) {
    var url = cleanUrl(btn.getAttribute('data-mc-share-url') || window.location.href);
    var title = btn.getAttribute('data-mc-share-title') || document.title;

    // navigator.share has to be called inside the user gesture, which this is.
    // Gate on a coarse pointer: desktop Safari offers navigator.share too, but
    // a copied link beats an OS share dialog when there is a keyboard present.
    if (typeof navigator.share === 'function' && coarsePointer()) {
      navigator.share({ title: title, url: url }).then(
        function () { track(btn, 'native'); },
        function () { /* user dismissed the sheet — no message, no fallback */ }
      );
      return;
    }

    copy(url, function (ok) {
      if (!ok) return;
      confirmCopy(btn);
      track(btn, 'clipboard');
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-mc-share]') : null;
    if (!btn) return;
    e.preventDefault();
    share(btn);
  });

  function boot() {
    if (liveRegion) return;
    liveRegion = document.createElement('span');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    document.body.appendChild(liveRegion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.MohairShare = { cleanUrl: cleanUrl };
})();
